'use client';

/**
 * Client-side error reporter.
 * Sends frontend errors to /api/client-error where they are appended to a daily log file.
 *
 * IMPORTANT: This intentionally does NOT mask data (as requested). Be careful with PII in production.
 */

const ENDPOINT = "/api/client-error";
const MAX_BREADCRUMBS = 40;

function safeGet(fn, fallback = null) {
  try { return fn(); } catch { return fallback; }
}

function getOrCreateTraceId() {
  // Prefer sessionStorage, fall back to in-memory random.
  const key = "client_trace_id";
  const existing = safeGet(() => sessionStorage.getItem(key));
  if (existing) return existing;

  const id = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  safeGet(() => sessionStorage.setItem(key, id));
  return id;
}

function nowIso() {
  return new Date().toISOString();
}

function buildEnvContext() {
  return {
    url: safeGet(() => window.location.href),
    referrer: safeGet(() => document.referrer),
    userAgent: safeGet(() => navigator.userAgent),
    language: safeGet(() => navigator.language),
    languages: safeGet(() => navigator.languages),
    platform: safeGet(() => navigator.platform),
    timezone: safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    screen: safeGet(() => ({
      w: window.screen?.width,
      h: window.screen?.height,
      dpr: window.devicePixelRatio,
    })),
    viewport: safeGet(() => ({
      w: window.innerWidth,
      h: window.innerHeight,
    })),
    online: safeGet(() => navigator.onLine),
    connection: safeGet(() => {
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!c) return null;
      return {
        effectiveType: c.effectiveType,
        downlink: c.downlink,
        rtt: c.rtt,
        saveData: c.saveData,
      };
    }),
    memory: safeGet(() => {
      // Chrome-only
      const m = performance?.memory;
      if (!m) return null;
      return {
        jsHeapSizeLimit: m.jsHeapSizeLimit,
        totalJSHeapSize: m.totalJSHeapSize,
        usedJSHeapSize: m.usedJSHeapSize,
      };
    }),
  };
}

function normalizeError(err) {
  if (!err) return { name: "UnknownError", message: "Unknown error", stack: null };

  // Errors, strings, objects
  const name = err.name || err.constructor?.name || "Error";
  const message = err.message || String(err);
  const stack = err.stack || null;

  // Axios etc sometimes attach response/request
  const extra = {};
  if (err.code) extra.code = err.code;
  if (err.status) extra.status = err.status;
  if (err.response?.status) extra.httpStatus = err.response.status;
  if (err.config?.url) extra.url = err.config.url;
  if (err.config?.method) extra.method = err.config.method;

  return { name, message, stack, extra };
}

let telemetryUser = null;

export function setTelemetryUser(user) {
  telemetryUser = user
    ? {
        idpersona: user.idpersona ?? null,
        cedularuc: user.cedularuc ?? null,
      }
    : null;
}

// Simple breadcrumb store (in-memory)
const breadcrumbs = [];
export function addBreadcrumb(bc) {
  try {
    breadcrumbs.push({ ts: nowIso(), ...bc });
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
  } catch {
    // intentionally ignored
  }
}

function payloadToBlob(payload) {
  return new Blob([JSON.stringify(payload)], { type: "application/json" });
}

export async function reportClientError({
  kind,
  error,
  context,
  tags,
  severity = "error",
}) {
  const traceId = getOrCreateTraceId();
  const eventId = `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

  const normalized = normalizeError(error);

  const payload = {
    ts: nowIso(),
    severity,
    kind: kind || "unknown",
    traceId,
    eventId,
    tags: tags || {},
    context: {
      ...buildEnvContext(),
      ...context,
    },
    error: normalized,
    breadcrumbs: breadcrumbs.slice(),
    sessionUser: telemetryUser,
  };

  // Try sendBeacon first (more reliable on unload/crash)
  const ok = safeGet(() => navigator.sendBeacon?.(ENDPOINT, payloadToBlob(payload)), false);
  if (ok) return { ok: true, eventId };

  // Fallback to fetch
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return { ok: res.ok, eventId };
  } catch {
    return { ok: false, eventId };
  }
}
